import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Drawer,
  List,
  Typography,
  Space,
  Button,
  Modal,
  Input,
  message,
  Tooltip,
  Tag,
  Empty,
  Divider,
  Card,
  Collapse,
} from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  FileOutlined,
  DownloadOutlined,
  EyeOutlined,
  LinkOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { formatDate } from "../../utils/format";
import { api } from "../../services/authService";

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

// Replace the normalizeDocumentName function with a more dynamic approach
const normalizeDocumentName = (name, displayNameFromApi) => {
  if (!name) return "";

  // 1. First priority: Use display name from API if available
  if (displayNameFromApi) {
    return displayNameFromApi;
  }

  // 2. Second priority: Check our special cases mapping
  const specialCases = {
    nin: "NIN",
    bvn: "BVN",
    business_reg_number: "Business Registration Number",
    proof_of_identity: "Proof of Identity",
    proof_of_address: "Proof of Address",
    proof_of_liveness: "Proof of Liveness",
    source_of_funds: "Source of Funds",
    incorporation_doc: "Certificate of Incorporation",
    directors: "Directors",
    shareholders: "Shareholders",
    business_profile: "Business Profile",
    intl_passport: "International Passport",
    drivers_license: "Driver's License",
    national_id: "National ID",
    voters_card: "Voter's Card",
    selfie_image: "Selfie Image",
    utility_bill: "Utility Bill",
    bank_statement: "Bank Statement",
    audited_financials: "Audited Financials",
    certificate_of_incorp: "Certificate of Incorporation",
  };

  const lowercaseName = name.toLowerCase();
  if (specialCases[lowercaseName]) {
    return specialCases[lowercaseName];
  }

  // 3. Third priority: Smart pattern-based formatting
  // Handle common patterns in credential names
  if (lowercaseName.includes("_verification")) {
    return (
      name
        .replace("_verification", "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") + " Verification"
    );
  }

  if (lowercaseName.includes("_certificate")) {
    return (
      name
        .replace("_certificate", "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") + " Certificate"
    );
  }

  // Special handling for IDs
  if (lowercaseName.includes("_id") && !lowercaseName.includes("identity")) {
    return (
      name
        .replace("_id", "")
        .split("_")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ") + " ID"
    );
  }

  // 4. Default: Transform snake_case to Title Case
  return name
    .split("_")
    .map((word) => {
      // Keep common abbreviations uppercase
      if (["id", "tin", "vat", "cac", "rc"].includes(word.toLowerCase())) {
        return word.toUpperCase();
      }
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

// Helper function to get document display name for list display
const getDocumentListName = (record) => {
  if (!record) return "";

  // Look for a display name property in the record
  const displayName = record.display_name || record.displayName || null;

  // Get normalized credential name with possible display name from API
  const credName = normalizeDocumentName(
    record.credential_name || record.name || "",
    displayName
  );

  return credName;
};

// Helper function to get document display name for preview modal
const getDocumentModalName = (record) => {
  if (!record) return "";

  // For documents, use the document's name property if available
  if (
    record.type === "document" &&
    record.credential_value &&
    typeof record.credential_value === "object" &&
    record.credential_value.name
  ) {
    return normalizeDocumentName(record.credential_value.name);
  }

  // Fallback to credential name
  return normalizeDocumentName(record.credential_name || record.name || "");
};

// Helper function to extract document type from document data
const getDocumentType = (record) => {
  if (!record || !record.credential_value) return null;

  if (
    typeof record.credential_value === "object" &&
    record.credential_value.document_type
  ) {
    return record.credential_value.document_type;
  }

  if (
    typeof record.credential_value === "object" &&
    record.credential_value.type
  ) {
    return record.credential_value.type;
  }

  return null;
};

const ComplianceDrawer = ({ visible, onClose, user, currentUser }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [declineReason, setDeclineReason] = useState("");
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBackUrl, setPreviewBackUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [activeTab, setActiveTab] = useState("front");
  const [complianceConfig, setComplianceConfig] = useState(null);

  // Fetch compliance configuration
  const fetchComplianceConfig = useCallback(async () => {
    try {
      const response = await api.get("/admin/compliance-configurations");
      setComplianceConfig(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching compliance configuration:", error);
      if (error.response && error.response.status === 401) {
        message.error("Session expired. Please login again.");
      } else {
        message.error("Failed to load compliance configuration");
      }
      return null;
    }
  }, []);

  const fetchData = useCallback(async () => {
    // Determine which ID to use based on user type
    let userId = null;
    let profileId = null;
    
    if (user?.business_id) {
      // Business user - use profile_id
      profileId = user.business_id;
    } else if (user?.id) {
      // Individual user - use user_id
      userId = user.id;
    }
    
    if (!userId && !profileId) {
      console.log("No user ID or profile ID available", user);
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        include_auto_verify: true,
      });
      
      // Add the appropriate ID parameter
      if (profileId) {
        params.set("profile_id", profileId);
      } else if (userId) {
        params.set("user_id", userId);
      }

      // Fetch both compliance records and configuration in parallel
      const [recordsResponse, config] = await Promise.all([
        api.get(`/admin/compliances?${params}`),
        fetchComplianceConfig(),
      ]);

      const records = recordsResponse.data.compliance_records || [];

      // Check for document names mapping in configuration
      const documentNameMappings =
        config && config.document_name_mappings
          ? config.document_name_mappings
          : {};

      // Transform records to include credential_name as a property
      const transformedRecords = records.map((record) => {
        // Check if there's a mapping in the configuration for this document
        const configDisplayName =
          documentNameMappings[record.credential_name || record.name];

        return {
          ...record,
          credential_name: record.credential_name || record.name,
          // Store display name from API or config if available
          display_name: record.display_name || configDisplayName || null,
          normalized_name: normalizeDocumentName(
            record.credential_name || record.name,
            record.display_name || configDisplayName
          ),
          // Add isEditable property
          isEditable: isRecordEditable(record, config),
        };
      });

      console.log("Transformed compliance records:", transformedRecords);
      setDocuments(transformedRecords);
    } catch (error) {
      console.error("Error fetching compliance records:", error);
      if (error.response && error.response.status === 401) {
        message.error("Session expired. Please login again.");
      } else {
        message.error("Failed to load compliance records");
      }
    } finally {
      setLoading(false);
    }
  }, [user, fetchComplianceConfig]);

  // Check if a record is editable based on compliance configuration
  const isRecordEditable = (record, config) => {
    // Don't allow modification if id starts with auto_verify_
    if (record.id && record.id.startsWith("auto_verify_")) {
      return false;
    }

    // Don't allow modification for certain categories
    if (config && config.auto_verify_records) {
      // Get country-specific config or default
      const country = user?.country || "default";
      const countryConfig =
        config.auto_verify_records[country] ||
        config.auto_verify_records.default;

      if (countryConfig) {
        // Check if category is in non-editable categories
        if (
          countryConfig.categories &&
          record.category &&
          countryConfig.categories.includes(record.category)
        ) {
          return false;
        }

        // Check if credential name is in non-editable credential names
        if (
          countryConfig.credential_names &&
          record.credential_name &&
          countryConfig.credential_names.includes(record.credential_name)
        ) {
          return false;
        }
      }
    }

    return true;
  };

  useEffect(() => {
    if (visible && user?.id) {
      fetchData();
    }
  }, [visible, user?.id, fetchData]);

  const handleDocumentAction = async (documentId, status) => {
    // Check if the document is editable before proceeding
    const document = documents.find((doc) => doc.id === documentId);
    if (!document || !document.isEditable) {
      message.error("This document cannot be modified");
      return;
    }

    if (status === "declined" && !declineReason) {
      setSelectedDoc(documentId);
      setDeclineModalVisible(true);
      return;
    }

    try {
      await api.patch(`/admin/compliances/${documentId}`, {
        status,
        ...(status === "declined" ? { reason: declineReason } : {}),
      });

      message.success(
        `Document ${status === "approved" ? "approved" : "rejected"}`
      );
      setDeclineModalVisible(false);
      setDeclineReason("");
      setSelectedDoc(null);
      fetchData();
    } catch (error) {
      console.error("Error updating document status:", error);
      if (error.response && error.response.status === 401) {
        message.error("Session expired. Please login again.");
      } else {
        message.error("Failed to update document status");
      }
    }
  };

  const canModifyCompliance = (record) => {
    return record.isEditable;
  };

  const handlePreview = (url, title, backUrl = null) => {
    setPreviewUrl(url);
    setPreviewBackUrl(backUrl);
    setPreviewTitle(title);
    setActiveTab("front");
    setPreviewModalVisible(true);
  };

  // Group documents by category
  const getDocumentsByCategory = useCallback(() => {
    const groupedDocs = {};

    // Include all documents for viewing, but mark which ones are editable
    documents.forEach((doc) => {
      const category = doc.category || "other";
      if (!groupedDocs[category]) {
        groupedDocs[category] = [];
      }
      groupedDocs[category].push(doc);
    });

    return groupedDocs;
  }, [documents]);

  // Get category display name
  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      "business-info": "Business Information",
      "business-doc": "Business Documents",
      "indv-data": "Individual Data",
      other: "Other Documents",
    };

    return (
      categoryNames[category] ||
      category
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    );
  };

  // Render document value based on type
  const renderDocumentValue = (record) => {
    // For regular documents (proof of identity, address, etc.)
    if (
      record.type === "document" &&
      record.credential_value &&
      typeof record.credential_value === "object"
    ) {
      const document = record.credential_value;
      const hasBackPage = !!document.backpage;
      // Use the document's name for the modal title
      const documentTitle = getDocumentModalName(record);

      return (
        <Space direction="vertical" size="small" style={{ width: "100%" }}>
          <Space>
            {document.frontpage && (
              <>
                <Button
                  type="link"
                  icon={<EyeOutlined />}
                  onClick={() =>
                    handlePreview(
                      document.frontpage.url,
                      documentTitle,
                      hasBackPage ? document.backpage.url : null
                    )
                  }
                >
                  View Document
                </Button>
                <Button
                  type="link"
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(document.frontpage.url, "_blank")}
                >
                  Download
                </Button>
              </>
            )}
          </Space>
        </Space>
      );
    }
    // For simple field credentials (NIN, BVN, etc.)
    else if (record.type === "field" || record.type === "credential") {
      // Special styling for identification numbers
      const isIdNumber =
        record.credential_name === "nin" ||
        record.credential_name === "bvn" ||
        record.credential_name === "business_reg_number";

      if (isIdNumber && record.credential_value) {
        return (
          <Tag
            style={{
              fontSize: "14px",
              padding: "2px 8px",
              fontFamily: "monospace",
              letterSpacing: "0.5px",
              backgroundColor: "#f5f5f5",
              border: "1px solid #d9d9d9",
              color: "#262626",
              margin: 0,
            }}
          >
            {record.credential_value}
          </Tag>
        );
      }

      // Regular credential value display
      return (
        <Space direction="vertical" style={{ width: "100%" }}>
          {record.credential_value && (
            <Text style={{ fontSize: "14px" }}>{record.credential_value}</Text>
          )}
        </Space>
      );
    }
    // Special handling for directors list
    else if (
      (record.credential_name === "directors" || record.name === "directors") &&
      record.credential_value
    ) {
      try {
        let directors = [];
        if (typeof record.credential_value === "string") {
          directors = JSON.parse(record.credential_value);
        } else if (Array.isArray(record.credential_value)) {
          directors = record.credential_value;
        }

        if (directors.length > 0) {
          return (
            <Space direction="vertical" style={{ width: "100%" }}>
              {directors.map((director, index) => (
                <Card
                  key={index}
                  size="small"
                  style={{
                    marginBottom: 8,
                    backgroundColor: "#fafafa",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ width: "100%" }}
                  >
                    <Text strong style={{ fontSize: "14px" }}>
                      {director.name || director.full_name}
                    </Text>
                    {director.position && (
                      <Text style={{ fontSize: "13px", color: "#595959" }}>
                        <Text strong style={{ color: "#262626" }}>
                          Position:
                        </Text>{" "}
                        {normalizeDocumentName(director.position)}
                      </Text>
                    )}
                  </Space>
                </Card>
              ))}
            </Space>
          );
        }
      } catch (e) {
        console.error("Error parsing directors:", e);
      }
    }
    // Any other type of data
    else if (record.credential_value) {
      if (typeof record.credential_value === "object") {
        // For complex objects, show a summary
        return (
          <Text style={{ fontSize: "14px" }}>
            {JSON.stringify(record.credential_value).substring(0, 100)}
            {JSON.stringify(record.credential_value).length > 100 ? "..." : ""}
          </Text>
        );
      } else {
        // For simple values
        return (
          <Text style={{ fontSize: "14px" }}>{record.credential_value}</Text>
        );
      }
    }

    return null;
  };

  const renderDocumentStatus = (status) => {
    const statuses = {
      pending: { color: "warning", text: "Pending Review" },
      "in-review": { color: "processing", text: "In Review" },
      approved: { color: "success", text: "Approved" },
      declined: { color: "error", text: "Declined" },
    };

    const statusInfo = statuses[status] || {
      color: "default",
      text: status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown",
    };

    return <Tag color={statusInfo.color}>{statusInfo.text}</Tag>;
  };

  // Render action buttons based on document status
  const renderActionButtons = (record) => {
    if (!canModifyCompliance(record)) {
      return null;
    }

    const { status } = record;

    return (
      <Space>
        {(status === "pending" || status === "declined") && (
          <Tooltip title="Approve">
            <Button
              type="text"
              icon={<CheckOutlined style={{ color: "#52c41a" }} />}
              onClick={() => handleDocumentAction(record.id, "approved")}
            />
          </Tooltip>
        )}

        {(status === "pending" || status === "approved") && (
          <Tooltip title="Decline">
            <Button
              type="text"
              icon={<CloseOutlined style={{ color: "#f5222d" }} />}
              onClick={() => handleDocumentAction(record.id, "declined")}
            />
          </Tooltip>
        )}
      </Space>
    );
  };

  const groupedDocuments = getDocumentsByCategory();
  const categories = Object.keys(groupedDocuments);
  
  console.log("Documents state:", documents);
  console.log("Grouped documents:", groupedDocuments);
  console.log("Categories:", categories);

  return (
    <>
      <Drawer
        title={<Title level={4}>Compliance Documents</Title>}
        placement="right"
        onClose={onClose}
        open={visible}
        width={800}
      >
        <div>
          {user?.business_name && (
            <div style={{ marginBottom: 16 }}>
              <Text strong>Business: </Text>
              <Text>{user.business_name}</Text>
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              Loading compliance documents...
            </div>
          ) : documents.length === 0 ? (
            <Empty description="No compliance documents found" />
          ) : (
            <Collapse defaultActiveKey={categories} ghost>
              {categories.map((category) => (
                <Panel
                  header={
                    <Text strong>{getCategoryDisplayName(category)}</Text>
                  }
                  key={category}
                >
                  <List
                    itemLayout="horizontal"
                    dataSource={groupedDocuments[category]}
                    renderItem={(item) => (
                      <List.Item
                        actions={[renderActionButtons(item)]}
                        style={{
                          padding: "12px 0",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <List.Item.Meta
                          title={
                            <Space>
                              {renderDocumentStatus(item.status)}
                              <span>{getDocumentListName(item)}</span>
                            </Space>
                          }
                          description={renderDocumentValue(item)}
                        />

                        {item.status === "declined" && item.reason && (
                          <div style={{ marginTop: 8, color: "#ff4d4f" }}>
                            <Text type="danger">
                              <InfoCircleOutlined /> Reason: {item.reason}
                            </Text>
                          </div>
                        )}
                      </List.Item>
                    )}
                  />
                </Panel>
              ))}
            </Collapse>
          )}
        </div>
      </Drawer>

      {/* Document Preview Modal */}
      <Modal
        title={previewTitle}
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width={800}
        centered
      >
        {previewBackUrl ? (
          <div>
            <div style={{ marginBottom: 16, textAlign: "center" }}>
              <Button
                type={activeTab === "front" ? "primary" : "default"}
                onClick={() => setActiveTab("front")}
                style={{ marginRight: 8 }}
              >
                Front Page
              </Button>
              <Button
                type={activeTab === "back" ? "primary" : "default"}
                onClick={() => setActiveTab("back")}
              >
                Back Page
              </Button>
            </div>
            <div style={{ textAlign: "center" }}>
              {activeTab === "front" ? (
                <img
                  src={previewUrl}
                  alt="Document Front"
                  style={{ maxWidth: "100%" }}
                />
              ) : (
                <img
                  src={previewBackUrl}
                  alt="Document Back"
                  style={{ maxWidth: "100%" }}
                />
              )}
            </div>
          </div>
        ) : (
          <div style={{ textAlign: "center" }}>
            <img src={previewUrl} alt="Document" style={{ maxWidth: "100%" }} />
          </div>
        )}
      </Modal>

      {/* Decline Reason Modal */}
      <Modal
        title="Provide Reason for Declining"
        open={declineModalVisible}
        onOk={() => handleDocumentAction(selectedDoc, "declined")}
        onCancel={() => {
          setDeclineModalVisible(false);
          setDeclineReason("");
          setSelectedDoc(null);
        }}
      >
        <TextArea
          rows={4}
          value={declineReason}
          onChange={(e) => setDeclineReason(e.target.value)}
          placeholder="Please provide a reason for declining this document"
        />
      </Modal>
    </>
  );
};

ComplianceDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  user: PropTypes.object,
  currentUser: PropTypes.object,
};

export default ComplianceDrawer;
